import { createSelector } from "reselect"
import { get, chain, pickBy, find, isEmpty, map, first } from "lodash"
import moment from "moment"
import { getAllItems, getCategories, getOrderedCategoryItems, getDisplayType } from "./items"
import { getFavoriteFactors } from "modules/portfolio-root/selectors/portfolios"
import { getExposure, getFilteredExposure, getFirst, getCurrent } from "./exposure"
import { computeCategoryItemsSubset, computeNonZerosKeys, computeOptions, computeDisplayCategories } from "helpers/analyze"
import { combineResourcesStatuses, isLoading } from "helpers/data-statuses"
import { makeComputeDriftFromData } from "helpers/drift"
import { getAllResourcesStatuses } from "selectors/data"
import { getContentSets } from "selectors/contentSets"

const EMPTY_ARRAY = []

const getContentSetId = (_, props) => get(props, "params.contentSetId", null)

export function computeData(items, exposure) {
  return chain(exposure)
    .map((datum, index) => {
      const { date } = datum
      const result = chain(items)
        .map(item => {
          return ([
            item.id,
            item.fn
              ? item.fn(datum)
              : get(datum, item.field, 0),
          ])
        })
        .filter(([, value]) => !Number.isNaN(value))
        .fromPairs()
        .value()

      result.date = moment(date)
      result.index = index

      return result
    })
    .filter(datum => datum)
    .value()
}

export const getData = createSelector(
  [getAllItems, getFilteredExposure],
  (items, exposure) => computeData(items, exposure),
)

const getNonZerosKeys = createSelector(
  [getData, getFavoriteFactors],
  computeNonZerosKeys,
)

export const getCategoryItemsSubset = createSelector(
  [getOrderedCategoryItems, getNonZerosKeys],
  computeCategoryItemsSubset,
)

export const getFilteredData = createSelector(
  [getData, getNonZerosKeys, getCategoryItemsSubset],
  (data, keys, items) => isEmpty(items) ? EMPTY_ARRAY : map(data, item => pickBy(item, (item, key) => keys[key])),
)

export const getDisplayCategories = createSelector(
  [getAllItems, getNonZerosKeys],
  computeDisplayCategories,
)

export const getOptions = createSelector(
  [getCategories, getFirst, getCurrent, getDisplayCategories],
  computeOptions,
)

export const getDrift = createSelector(
  [getExposure],
  makeComputeDriftFromData(item => item.factors),
)

export const getSelectedOption = createSelector(
  [getOptions, getDisplayType],
  (options, displayType) => find(options, { id: displayType }) || first(options),
)

export const getPassthroughResourcesStatus = createSelector(
  [getAllResourcesStatuses],
  (statuses) => combineResourcesStatuses("exposure", statuses),
)

export const getIsLoading = createSelector(
  [getPassthroughResourcesStatus],
  isLoading,
)

export const getIsContentSetAndUnavailable = createSelector(
  [getContentSets, getContentSetId],
  (contentSets, contentSetId) => contentSetId && !find(contentSets, { id: contentSetId }),
)
